import { useEffect, useState } from "react";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { WebTracerProvider } from "@opentelemetry/sdk-trace-web";
import { BatchSpanProcessor } from "@opentelemetry/sdk-trace-base";
import { OTLPTraceExporter } from "@opentelemetry/exporter-trace-otlp-http";
import { resourceFromAttributes } from "@opentelemetry/resources";
import { registerInstrumentations } from "@opentelemetry/instrumentation";
import { FetchInstrumentation } from "@opentelemetry/instrumentation-fetch";
import { XMLHttpRequestInstrumentation } from "@opentelemetry/instrumentation-xml-http-request";

// Browsers cannot reach the in-cluster agent, so the host must route /otlp/* to it.
const OTLP_TRACES_URL = "/otlp/v1/traces";

// A wildcard breaks third-party CORS preflights (e.g. LaunchDarkly).
export const TRACE_PROPAGATION_URL_PATTERNS = [
  /^https?:\/\/([\w.-]+\.)?cityba\.se(?::\d+)?\//i,
  /^https?:\/\/([\w.-]+\.)?thecitybase\.com(?::\d+)?\//i,
  /^https?:\/\/localhost(?::\d+)?\//i,
  /^https?:\/\/127\.0\.0\.1(?::\d+)?\//i
];

export const OTEL_LD_FLAG = "enable-otel-tracing";

const OTEL_INIT_STATE = {
  NOT_STARTED: "not_started",
  GLOBAL_REGISTERED: "global_registered",
  COMPLETE: "complete"
};

let otelInitState = OTEL_INIT_STATE.NOT_STARTED;

export function initOpenTelemetry() {
  // provider.register() mutates global OTel state, so never run it twice.
  if (otelInitState !== OTEL_INIT_STATE.NOT_STARTED) {
    return;
  }

  try {
    const resource = resourceFromAttributes({
      "service.name": "navigate-frontend-traces",
      "service.version":
        (typeof process !== "undefined" &&
          process.env &&
          process.env.COMMIT_SHA) ||
        "unknown"
    });

    const exporter = new OTLPTraceExporter({
      url: OTLP_TRACES_URL
    });

    const provider = new WebTracerProvider({
      resource,
      spanProcessors: [new BatchSpanProcessor(exporter)]
    });

    provider.register();
    otelInitState = OTEL_INIT_STATE.GLOBAL_REGISTERED;

    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: TRACE_PROPAGATION_URL_PATTERNS
        }),
        new XMLHttpRequestInstrumentation({
          propagateTraceHeaderCorsUrls: TRACE_PROPAGATION_URL_PATTERNS
        })
      ]
    });

    otelInitState = OTEL_INIT_STATE.COMPLETE;
  } catch (error) {
    console.error(
      "[OpenTelemetry] Failed to initialize instrumentation:",
      error
    );
  }
}

// Holds children until LD is ready so early HTTP requests are instrumented.
export function OpenTelemetryGate({ children }) {
  const ldClient = useLDClient();
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    let enabled = false;
    try {
      const detail = ldClient.variationDetail(OTEL_LD_FLAG, false);
      enabled = Boolean(detail.value);
    } catch (error) {
      console.warn(
        `[OpenTelemetry] LD flag '${OTEL_LD_FLAG}' evaluation failed; skipping instrumentation`,
        error
      );
    }

    if (enabled) {
      initOpenTelemetry();
    }

    setReady(true);
  }, [ldClient]);

  if (!ready) {
    return null;
  }

  return children;
}
